import React from 'react';
import { Globe, DollarSign, ShieldCheck, TrendingUp, Landmark } from 'lucide-react';
import ProductInquiryCard from '../../components/ProductInquiryCard';

const Offshore = () => {
  const globalPillars = [
    {
      title: "Direct US Mega-Cap Equity Access",
      desc: "Invest in fractional shares of global technology leaders including Apple, Microsoft, NVIDIA, Alphabet, and Amazon with zero account maintenance charges.",
      badge: "Tech & Growth",
      metric: "NASDAQ & S&P 500",
      icon: <TrendingUp className="w-5 h-5 text-[#60A5FA]" />
    },
    {
      title: "Currency Depreciation Hedge (USD)",
      desc: "Shield your long-term wealth against historical Rupee depreciation (~3-5% annualized) by holding capital in high-stability US Dollar instruments.",
      badge: "Macro Shield",
      metric: "USD Capital Base",
      icon: <DollarSign className="w-5 h-5 text-[#60A5FA]" />
    },
    {
      title: "Full RBI LRS Compliance Architecture",
      desc: "Completely legitimate and transparent remittance architecture under the RBI Liberalised Remittance Scheme (LRS) allowing up to $250,000 per financial year.",
      badge: "Regulatory Shield",
      metric: "Up to $250k/Year",
      icon: <Landmark className="w-5 h-5 text-[#60A5FA]" />
    },
    {
      title: "Global Thematic ETFs & Sovereign Debt",
      desc: "Gain diversified exposure to international real estate (REITs), global semiconductor indices, clean energy, and US Treasury yields yielding 4.5%–5.2%.",
      badge: "Thematic Basket",
      metric: "Global ETFs",
      icon: <Globe className="w-5 h-5 text-[#60A5FA]" />
    }
  ];

  return (
    <div className="min-h-screen bg-[#07131F] text-slate-100 selection:bg-[#16325B] selection:text-white pb-20">
      
      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 border-b border-slate-800/80 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(#3B82F6_1px,transparent_1px)] [background-size:24px_24px]"></div>
        
        <div className="max-w-5xl mx-auto relative z-10 text-center">
          {/* <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#16325B]/40 border border-blue-500/30 text-[#60A5FA] text-xs sm:text-sm font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-[#60A5FA] animate-pulse"></span>
            RBI LRS Route • US & Global Market Portals
          </div> */}

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif font-bold text-white tracking-tight leading-tight max-w-4xl mx-auto">
            International & <span className="text-[#60A5FA]">Offshore Investing</span>
          </h1>

          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Diversify beyond Indian domestic equities. Harness global innovation, hold hard USD currency assets, and protect your capital against macroeconomic volatility.
          </p>

          <div className="mt-8 flex justify-center gap-4">
            <a
              href="#inquire"
              className="px-8 py-3.5 rounded-xl bg-gradient-to-r from-[#16325B] to-[#2563EB] hover:from-[#1D4ED8] hover:to-[#3B82F6] text-white font-bold transition-all duration-300 shadow-lg shadow-[#16325B]/40 hover:shadow-blue-500/30 hover:-translate-y-0.5 border border-blue-400/20"
            >
              Initiate Global Account Setup
            </a>
          </div>
        </div>
      </section>

      {/* 2. STATS OVERVIEW BAR */}
      <section className="py-10 bg-[#091B2C] border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
          <div className="p-4 bg-[#0B1E30]/70 rounded-2xl border border-slate-700/60 shadow-lg">
            <div className="text-xs uppercase text-slate-400 font-semibold tracking-wider">Annual LRS Cap</div>
            <div className="text-2xl font-bold text-[#60A5FA] font-mono mt-1">$250,000 / Person</div>
          </div>
          <div className="p-4 bg-[#0B1E30]/70 rounded-2xl border border-slate-700/60 shadow-lg">
            <div className="text-xs uppercase text-slate-400 font-semibold tracking-wider">Starting Ticket Size</div>
            <div className="text-2xl font-bold text-white font-serif mt-1">$1,000+</div>
          </div>
          <div className="p-4 bg-[#0B1E30]/70 rounded-2xl border border-slate-700/60 shadow-lg">
            <div className="text-xs uppercase text-slate-400 font-semibold tracking-wider">Execution Standard</div>
            <div className="text-2xl font-bold text-blue-300 font-serif mt-1">SIPC Insured (Up to $500k)</div>
          </div>
        </div>
      </section>

      {/* 3. CORE STRATEGY & DYNAMIC INQUIRY CARD */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Left Column: Strategic Advantages */}
          <div className="lg:col-span-7 space-y-5">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white mb-6">
              Global Diversification Pillars
            </h2>

            <div className="space-y-4">
              {globalPillars.map((item, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl bg-[#0B1E30]/80 backdrop-blur-sm border border-slate-700/60 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#16325B]/40 border border-blue-500/30 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <h3 className="text-lg font-bold text-white tracking-tight">{item.title}</h3>
                    </div>
                    <span className="text-[11px] font-mono font-semibold px-2.5 py-1 rounded-full bg-[#16325B]/40 text-[#60A5FA] border border-blue-500/25">
                      {item.metric}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed pl-13">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Regulatory FAQ Note */}
            <div className="mt-8 p-5 rounded-2xl bg-[#081523] border border-slate-800 text-xs text-slate-400 leading-relaxed">
              <div className="flex items-center gap-2 text-[#60A5FA] font-semibold mb-1">
                <ShieldCheck className="w-4 h-4 text-[#60A5FA]" />
                <span>Regulatory Notice for Indian Residents</span>
              </div>
              All overseas transactions and remittances are routed via Authorized Dealer Category-I (AD-I) banks under RBI Master Directions on LRS. Direct US broker accounts are protected under SIPC guidelines.
            </div>
          </div>

          {/* Right Column: Premium Animated Form */}
          <div id="inquire" className="lg:col-span-5 sticky top-24">
            <ProductInquiryCard
              title="Open International Account"
              subtitle="Get guided assistance on RBI Form A2 remittance, bank KYC, and USD equity portfolio deployment."
              productName="International / Offshore Investing"
              defaultTicket="$2,500 to $10,000"
              buttonText="Initiate Global Onboarding"
            />
          </div>

        </div>
      </section>

    </div>
  );
};

export default Offshore;